type SelectionHead = { readonly selectionCut: number; readonly selectionDigest: string };
type Session =
  | { readonly worldKey: string; readonly sessionId: string }
  | { readonly environmentId: string; readonly threadId: string };

export interface SelectionOperation {
  readonly expected: SelectionHead | undefined;
  readonly failed: boolean;
  accept(head: SelectionHead): void;
  refuse(): void;
}

const sessions = new Map<
  string,
  {
    tail: Promise<void>;
    head: SelectionHead | undefined;
    failed: boolean;
  }
>();

/** A gesture and Send reserve their place synchronously at the interaction.
 * Only a preceding operation's verified receipt advances the expected cut.
 * A later browse/refresh cannot silently substitute another client's choices. */
export function orderSessionSelection<T>(
  session: Session,
  displayed: SelectionHead | undefined,
  operation: (order: SelectionOperation) => Promise<T>,
): Promise<T> {
  const key = JSON.stringify(
    "worldKey" in session
      ? ["world", session.worldKey, session.sessionId]
      : ["local", session.environmentId, session.threadId],
  );
  const state = sessions.get(key) ?? { tail: Promise.resolve(), head: displayed, failed: false };
  sessions.set(key, state);
  const work = state.tail.then(() =>
    operation({
      expected: state.head,
      failed: state.failed,
      accept: (head) => {
        state.head = head;
      },
      refuse: () => {
        state.failed = true;
      },
    }),
  );
  const tail = work.then(
    () => undefined,
    () => {
      state.failed = true;
    },
  );
  state.tail = tail;
  void tail.then(() => {
    if (sessions.get(key) === state && state.tail === tail) sessions.delete(key);
  });
  return work;
}
